import { Form } from 'antd';
import { t } from 'i18next';
import { useEffect } from 'react';
import invariant from 'tiny-invariant';

import { useAppContext } from 'business/contextProviders/useAppContext';
import {
  useReportActivityAnalyzeActivities,
  useReportActivityAnalyzeCheckedActivitiesFormName,
} from 'business/shift-report/report-analyze/pages/activity-analyze/providers/filters';
import { ActivityAnalyzeCheckedActivities } from 'business/shift-report/report-analyze/pages/activity-analyze/providers/filters/types';
import { isUndefinedOrNull } from 'technical/utils/is-null-or-undefined';
import ReportAnalyzeCheckableTreeInputProps from 'ui/form/checkable-tree-input';
import Loader from 'ui/loader';

import { ActivityTreeMetadata } from './use-activity-tree';

type AsideActivityTreeProps = {
  metadata: ActivityTreeMetadata;
};

type FormValues = {
  checkedActivities: ActivityAnalyzeCheckedActivities;
};

const AsideActivityTree = ({ metadata }: AsideActivityTreeProps) => {
  const { currentConstructionSite } = useAppContext();

  invariant(currentConstructionSite, 'No constructionSiteId');

  const [form] = Form.useForm<FormValues>();
  const formName = useReportActivityAnalyzeCheckedActivitiesFormName();
  const providerValues = useReportActivityAnalyzeActivities();

  const { displayedActivities, allActivitiesChecked, error, loading } =
    metadata;

  // This is required to sync provider values to form values
  // Activities filters are dependent from the network, and not possible
  // manually push info from provider to form
  const onValuesChange = (values: FormValues) => {
    if (values.checkedActivities && values.checkedActivities.length === 0) {
      form.setFieldValue('checkedActivities', allActivitiesChecked);
    }
  };

  const currentValue = form.getFieldValue('checkedActivities');
  useEffect(() => {
    if (
      (isUndefinedOrNull(currentValue) || currentValue.length === 0) &&
      providerValues.length > 0
    ) {
      form.setFieldValue('checkedActivities', providerValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentConstructionSite.id,
    providerValues.length,
    currentValue?.length,
    form,
  ]);

  if (error) {
    <div>{t('errors.error_generic')}</div>;
  }

  if (loading) {
    return <Loader />;
  }

  return (
    <Form
      form={form}
      name={formName}
      initialValues={{ checkedActivities: providerValues }}
      onValuesChange={onValuesChange}
    >
      <Form.Item name="checkedActivities" noStyle>
        <ReportAnalyzeCheckableTreeInputProps data={displayedActivities} />
      </Form.Item>
    </Form>
  );
};

export default AsideActivityTree;
